import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Download, Users, Plane, TrainFront, MapPin, Mountain } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const PACKAGE_LABELS = { "kochi-to-kochi": "Kochi to Kochi", flight: "Flight", train: "Train", bhutan: "Bhutan" };
const inputCls =
  "w-full border-b border-vine bg-transparent py-3 text-sm text-cream placeholder:text-sage/50 focus:border-marigold focus:outline-none transition-colors duration-300";

const formatDetail = (d) => (typeof d === "string" ? d : "Something went wrong");

const fmtDate = (v) => {
  const d = new Date(v);
  return isNaN(d)
    ? "—"
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = logged out
  const [tab, setTab] = useState("kerala");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");

  const loadLeads = useCallback(async (which) => {
    setLoading(true);
    try {
      const url = which === "bhutan" ? `${API}/admin/bhutan-leads` : `${API}/leads`;
      const { data } = await axios.get(url);
      setLeads(data);
    } catch (err) {
      setLeads([]);
      toast.error(formatDetail(err.response?.data?.detail) || "Could not load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then(({ data }) => setAuth(data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth) loadLeads(tab);
  }, [tab, auth, loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      setAuth(data);
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } finally {
      setAuth(false);
      setLeads([]);
    }
  };

  const exportCsv = () => {
    const header = ["Date", "Name", "Phone", "Email", "Package", "Message"];
    const rows = leads.map((l) => [
      fmtDate(l.created_at),
      l.name,
      l.phone,
      l.email,
      PACKAGE_LABELS[l.package] || l.package,
      (l.message || "").replace(/[\r\n]+/g, " "),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `trekpanda-${tab}-leads.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-jungle" data-testid="admin-loading">
        <Loader2 className="animate-spin text-marigold" size={28} />
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-jungle px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-vine bg-moss p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-2.5">
            <img src="/trekpanda-mascot.png" alt="TrekPanda" className="h-9 w-auto" />
            <span className="font-sans text-base font-extrabold tracking-[0.25em] text-cream">
              TREK<span className="text-marigold">PANDA</span>
            </span>
          </div>
          <p className="mt-2 text-xs uppercase tracking-[0.25em] text-sage">Admin — Leads Dashboard</p>
          <div className="mt-8 space-y-6">
            <div>
              <label htmlFor="admin-email" className="text-[11px] uppercase tracking-[0.25em] text-sage">Email</label>
              <input id="admin-email" type="email" required data-testid="admin-email-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@trekpanda.in" className={inputCls} />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-[11px] uppercase tracking-[0.25em] text-sage">Password</label>
              <input id="admin-password" type="password" required data-testid="admin-password-input" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className={inputCls} />
            </div>
          </div>
          {error && <p className="mt-4 text-xs text-red-600" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="mt-8 flex w-full items-center justify-center gap-2 bg-marigold py-3.5 text-xs font-bold uppercase tracking-[0.25em] text-jungle transition-colors duration-300 hover:bg-marigold-hover disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <a href="/" className="mt-5 block text-center text-[11px] uppercase tracking-[0.2em] text-sage transition-colors hover:text-marigold">
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  const counts = leads.reduce((acc, l) => {
    acc[l.package] = (acc[l.package] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-jungle" data-testid="admin-dashboard">
      <header className="border-b border-vine bg-moss/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <img src="/trekpanda-mascot.png" alt="TrekPanda" className="h-9 w-auto" />
            <span className="font-sans text-base font-extrabold tracking-[0.25em] text-cream">
              TREK<span className="text-marigold">PANDA</span>
            </span>
            <span className="ml-3 border border-vine px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] text-sage">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-[11px] uppercase tracking-[0.2em] text-sage transition-colors hover:text-marigold">View Site</a>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-vine px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-sage transition-colors hover:border-marigold hover:text-marigold">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex gap-2" data-testid="admin-trip-tabs">
          <button
            onClick={() => setTab("kerala")}
            data-testid="tab-kerala"
            className={`rounded-full px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "kerala" ? "bg-marigold text-jungle" : "border border-vine text-sage hover:border-marigold hover:text-marigold"
            }`}
          >
            Kerala with Kanyakumari
          </button>
          <button
            onClick={() => setTab("bhutan")}
            data-testid="tab-bhutan"
            className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "bhutan" ? "bg-marigold text-jungle" : "border border-vine text-sage hover:border-marigold hover:text-marigold"
            }`}
          >
            <Mountain size={13} /> Bhutan
          </button>
        </div>

        <div className={`mt-6 grid gap-px border border-vine bg-vine ${tab === "kerala" ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1"}`} data-testid="admin-stats">
          <div className="bg-moss p-6">
            <Users size={18} className="text-marigold" />
            <p className="mt-3 font-serif text-4xl font-medium text-cream" data-testid="stat-total">{leads.length}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-sage">Total Enquiries</p>
          </div>
          {tab === "kerala" &&
            [
              { key: "kochi-to-kochi", icon: MapPin },
              { key: "flight", icon: Plane },
              { key: "train", icon: TrainFront },
            ].map(({ key, icon: Icon }) => (
              <div key={key} className="bg-moss p-6">
                <Icon size={18} className="text-marigold" />
                <p className="mt-3 font-serif text-4xl font-medium text-cream" data-testid={`stat-${key}`}>{counts[key] || 0}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-sage">{PACKAGE_LABELS[key]}</p>
              </div>
            ))}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h1 className="font-serif text-2xl font-medium text-cream">
            {tab === "kerala" ? "Kerala Enquiries" : "Bhutan Enquiries"}
          </h1>
          <div className="flex gap-3">
            <button onClick={() => loadLeads(tab)} disabled={loading} data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-vine px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-sage transition-colors hover:border-marigold hover:text-marigold disabled:opacity-50">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCsv} disabled={!leads.length} data-testid="admin-export-button"
              className="flex items-center gap-2 bg-marigold px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-jungle transition-colors hover:bg-marigold-hover disabled:opacity-50">
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto border border-vine bg-moss" data-testid="admin-leads-table">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-vine text-[10px] uppercase tracking-[0.25em] text-sage">
                <th className="px-5 py-4 font-semibold">Date</th>
                <th className="px-5 py-4 font-semibold">Name</th>
                <th className="px-5 py-4 font-semibold">Phone</th>
                <th className="px-5 py-4 font-semibold">Email</th>
                <th className="px-5 py-4 font-semibold">Package</th>
                <th className="px-5 py-4 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-sage" data-testid="admin-empty-state">
                    {loading ? "Loading…" : tab === "kerala" ? "No enquiries yet — share the page and they'll land here." : "No Bhutan enquiries to show yet."}
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-vine last:border-0 hover:bg-jungle/60" data-testid={`lead-row-${l.id}`}>
                  <td className="whitespace-nowrap px-5 py-4 text-xs text-sage">{fmtDate(l.created_at)}</td>
                  <td className="px-5 py-4 font-semibold text-cream">{l.name}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-sage">{l.phone}</td>
                  <td className="px-5 py-4 text-sage">{l.email}</td>
                  <td className="px-5 py-4">
                    <span className="border border-marigold/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-marigold">
                      {PACKAGE_LABELS[l.package] || l.package}
                    </span>
                  </td>
                  <td className="max-w-[220px] truncate px-5 py-4 text-xs text-sage" title={l.message}>{l.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
